import {
  BgScreenThemeClass,
  READING_THEME_KEY,
  ReadingScreenTheme,
  RoutePath,
} from "@app/constants";
import HeartIcon from "@app/icons/heart";
import BookNoteModal from "@app/components/book-note-modal/book-note-modal.component";
import PurchaseMediaConfirmModal from "@app/components/purchase-media-confirm-modal";
import { addToast } from "@app/components/toast/toast.service";
import { openPortalDialog } from "@app/services/modal.service";
import BookService from "@app/services/http/book.service";
import PurchaseService from "@app/services/http/purchase.service";
import {
  BookDetail,
  BookElementDetail,
  BookMediaType,
  EbookManifest,
  EbookNote,
  EbookSession,
} from "@app/types";
import { Images } from "@assets/images";
import StorageService from "@core/services/storage";
import clsx from "clsx";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import { TbEdit } from "react-icons/tb";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectAuth } from "@app/store/auth";
import {
  AudiobookData,
  selectAudiobook,
  sendAudiobook,
  showAudiobookPanel,
} from "@app/store/audiobook";

const getDisplayAuthor = (book?: BookDetail): string => {
  if (!book) return "";

  if (book.authors?.length) {
    return book.authors.map((a) => a.fullName).filter(Boolean).join(", ");
  }

  return book.author ?? "";
};

const getElementAudioLink = (item: BookElementDetail): string => {
  if (item.linkAudio) return item.linkAudio;

  return item.audios?.find((a) => !!a.audio)?.audio ?? "";
};

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type SideBarTab = "chapter" | "note" | "highlight";

type SelectionState = {
  text: string;
  startOffset: number;
  endOffset: number;
  top: number;
  left: number;
};

const PDF_PAGE_WIDTH = 920;

const MemoizedPdfPage = memo(function PdfPageRenderer({
  pdfUrl,
  pageNumber,
  onRenderSuccess,
  onLoadError,
}: {
  pdfUrl: string;
  pageNumber: number;
  onRenderSuccess: () => void;
  onLoadError: () => void;
}) {
  return (
    <Document
      file={pdfUrl}
      loading={<Skeleton className="h-[1200px] w-full" />}
      onLoadError={onLoadError}
    >
      <Page
        pageNumber={pageNumber}
        width={PDF_PAGE_WIDTH}
        renderAnnotationLayer={false}
        renderTextLayer
        loading={<Skeleton className="h-[1200px] w-full" />}
        onRenderTextLayerSuccess={onRenderSuccess}
      />
    </Document>
  );
});

function EbookReadingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const pageContainerRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [book, setBook] = useState<BookDetail>();
  const [session, setSession] = useState<EbookSession>();
  const [manifest, setManifest] = useState<EbookManifest>();
  const [pdfUrl, setPdfUrl] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState("1");
  const [theme, setTheme] = useState(ReadingScreenTheme.DEFAULT);
  const [isListViewShow, setIsListViewShow] = useState(false);
  const [typeSideBarList, setTypeSideBarList] = useState<SideBarTab>("chapter");
  const [notes, setNotes] = useState<EbookNote[]>([]);
  const [selectionState, setSelectionState] = useState<SelectionState | null>(null);
  const [activeNoteId, setActiveNoteId] = useState<number | null>(null);
  const [textLayerVersion, setTextLayerVersion] = useState(0);

  const bookId = Number(id);

  const dispatch = useDispatch();
  const userInfo = useSelector(selectAuth);
  const audiobookState = useSelector(selectAudiobook);

  const sortedNotes = useMemo(
    () =>
      [...notes].sort((firstNote, secondNote) => {
        if (firstNote.pageNo !== secondNote.pageNo) {
          return firstNote.pageNo - secondNote.pageNo;
        }

        return firstNote.createdDate - secondNote.createdDate;
      }),
    [notes]
  );

  const getTextLayerElement = () =>
    pageContainerRef.current?.querySelector(
      ".react-pdf__Page__textContent"
    ) as HTMLDivElement | null;

  const clearSelection = () => {
    setSelectionState(null);
    window.getSelection()?.removeAllRanges();
  };

  const refreshSession = async (nextBookId: number) => {
    const response = await BookService.createEbookSession(nextBookId);

    setSession(response.data);

    return response.data;
  };

  const ensureSession = async (nextBookId: number) => {
    if (!session || session.expiredAt - Date.now() <= 30_000) {
      return refreshSession(nextBookId);
    }

    return session;
  };

  const refreshNotes = async (nextBookId: number) => {
    try {
      const response = await BookService.getEbookNotes(nextBookId);

      setNotes(response.data.items ?? []);
    } catch (_error) {
      setNotes([]);
    }
  };

  const loadPdf = async (nextBookId: number, nextSession?: EbookSession) => {
    setPdfLoading(true);

    try {
      const activeSession = nextSession ?? (await ensureSession(nextBookId));
      const blob = await BookService.getEbookPdf(nextBookId, activeSession.token);
      const nextPdfUrl = window.URL.createObjectURL(blob);

      setPdfUrl((previousPdfUrl) => {
        if (previousPdfUrl) {
          window.URL.revokeObjectURL(previousPdfUrl);
        }

        return nextPdfUrl;
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const initializeReader = async (nextBookId: number) => {
    setLoading(true);

    try {
      const detailResponse = await BookService.getBookById(nextBookId);
      const detail = detailResponse.data;

      if (!detail.ebook?.enabled || !detail.ebook?.hasAccess) {
        addToast({
          text: "Bạn chưa có quyền truy cập ebook của sách này",
          status: "warn",
        });
        navigate(`/${RoutePath.BOOK_DETAIL}/${nextBookId}`);

        return;
      }

      setBook(detail);

      const [nextSession, progressResponse] = await Promise.all([
        refreshSession(nextBookId),
        BookService.getEbookProgress(nextBookId).catch(() => null),
      ]);
      const manifestResponse = await BookService.getEbookManifest(
        nextBookId,
        nextSession.token
      );
      const nextManifest = manifestResponse.data;
      const initialPage =
        progressResponse?.data?.currentPage ||
        nextManifest.pages?.[0]?.page ||
        1;

      setManifest(nextManifest);
      setCurrentPage(initialPage);
      await Promise.all([refreshNotes(nextBookId), loadPdf(nextBookId, nextSession)]);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate(`/${RoutePath.BOOK_DETAIL}/${bookId}`);
  };

  const handleListViewClick = () => {
    clearSelection();
    setIsListViewShow((previousValue) => !previousValue);
  };

  const handleNightModeClick = () => {
    const nextTheme =
      theme === ReadingScreenTheme.DARK
        ? ReadingScreenTheme.DEFAULT
        : ReadingScreenTheme.DARK;

    setTheme(nextTheme);
    StorageService.set(READING_THEME_KEY, nextTheme);
  };

  const handlePreviousClick = () => {
    setCurrentPage((previousPage) => Math.max(1, previousPage - 1));
    setActiveNoteId(null);
    clearSelection();
  };

  const navigateToPage = (nextPage: number) => {
    if (!manifest?.pageCount) {
      return;
    }

    const normalizedPage = Math.min(
      manifest.pageCount,
      Math.max(1, Math.floor(nextPage))
    );

    setCurrentPage(normalizedPage);
    setPageInputValue(String(normalizedPage));
    setActiveNoteId(null);
    clearSelection();
  };

  const handleNextClick = () => {
    setCurrentPage((previousPage) =>
      Math.min(manifest?.pageCount || previousPage, previousPage + 1)
    );
    setActiveNoteId(null);
    clearSelection();
  };

  const handleListenBookClick = () => {
    if (!book?.audio?.enabled) {
      return;
    }

    if (!book.audio.hasAccess) {
      if (book.audio.canPurchase) {
        const currentKas = Number(userInfo.point ?? 0);
        const requiredKas = Number(book.audio.price ?? 0);
        const purchaseModal = openPortalDialog(PurchaseMediaConfirmModal, {
          bookName: book.name,
          mediaLabel: "Audio",
          currentKas,
          requiredKas,
        });

        purchaseModal.afterClosed().subscribe((data) => {
          if (!data?.isAccept) {
            return;
          }

          if (!data.isEnoughKas) {
            navigate(RoutePath.VAS_OPTION_LIST);

            return;
          }

          PurchaseService.purchaseBookByMediaType(book.id, BookMediaType.AUDIO).then(() => {
            addToast({ text: "Mua audio thành công" });
            BookService.getBookById(bookId).then((res) => setBook(res.data));
          });
        });

        return;
      }

      navigate(`/${RoutePath.BOOK_DETAIL}/${bookId}`);

      return;
    }

    if (audiobookState.audiobookData.bookId === book.id) {
      dispatch(showAudiobookPanel());

      return;
    }

    const audiobookData: AudiobookData = {
      bookId: book.id,
      bookName: book.name,
      author: getDisplayAuthor(book),
      thumbnail: book.thumbnails[0],
      audioList:
        book.elements
          ?.filter((item) => !!getElementAudioLink(item))
          ?.map((item) => ({
            id: item.id!,
            name: item.name,
            link: getElementAudioLink(item),
          })) ?? [],
    };

    dispatch(showAudiobookPanel());
    dispatch(sendAudiobook(audiobookData));
  };

  const openCreateNoteModal = () => {
    if (!selectionState) {
      return;
    }

    const bookNoteModalObs = openPortalDialog(BookNoteModal, {
      title: "Ghi chú",
    });

    bookNoteModalObs.afterClosed().subscribe(async (data) => {
      if (!data?.textNote) {
        return;
      }

      const response = await BookService.createEbookNote({
        bookId,
        pageNo: currentPage,
        textSelected: selectionState.text,
        textNote: data.textNote,
        anchorJson: {
          page: currentPage,
          startOffset: selectionState.startOffset,
          endOffset: selectionState.endOffset,
        },
      });

      addToast({ text: "Tạo ghi chú thành công" });
      setActiveNoteId(response.data.id);
      await refreshNotes(bookId);
      clearSelection();
      setTypeSideBarList("note");
      setIsListViewShow(true);
    });
  };

  const handleEditNote = (note: EbookNote) => {
    const bookNoteModalObs = openPortalDialog(BookNoteModal, {
      title: "Cập nhật ghi chú",
      initialTextNote: note.textNote,
    });

    bookNoteModalObs.afterClosed().subscribe(async (data) => {
      if (!data?.textNote) {
        return;
      }

      await BookService.updateEbookNote(note.id, {
        textNote: data.textNote,
      });
      addToast({ text: "Cập nhật ghi chú thành công" });
      setActiveNoteId(note.id);
      await refreshNotes(bookId);
    });
  };

  const handleDeleteNote = async (noteId: number) => {
    try {
      await BookService.deleteEbookNote(noteId);
      addToast({ text: "Xóa ghi chú thành công" });

      if (activeNoteId === noteId) {
        setActiveNoteId(null);
      }

      await refreshNotes(bookId);
    } catch (_error) {
      addToast({ text: "Xóa ghi chú thất bại", status: "inValid" });
    }
  };

  const handleLikeClick = async () => {
    if (!book) return;

    const wasFavorite = book.isFavorite;

    setBook((prev) =>
      prev
        ? {
            ...prev,
            isFavorite: !wasFavorite,
            insight: prev.insight
              ? {
                  ...prev.insight,
                  likeCount: prev.insight.likeCount + (wasFavorite ? -1 : 1),
                }
              : prev.insight,
          }
        : prev
    );

    try {
      if (wasFavorite) {
        await BookService.deleteFavorite(book.id);
      } else {
        await BookService.createFavorite(book.id);
      }
    } catch (_error) {
      setBook((prev) =>
        prev
          ? {
              ...prev,
              isFavorite: wasFavorite,
              insight: prev.insight
                ? {
                    ...prev.insight,
                    likeCount: prev.insight.likeCount + (wasFavorite ? 1 : -1),
                  }
                : prev.insight,
            }
          : prev
      );
    }
  };

  const handleNavigateToNote = (note: EbookNote) => {
    setTypeSideBarList("note");
    setIsListViewShow(true);
    setActiveNoteId(note.id);
    clearSelection();
    setCurrentPage(note.pageNo);
  };

  const handleTextLayerSuccess = useCallback(() => {
    setTextLayerVersion((v) => v + 1);
  }, []);

  const handlePdfLoadError = useCallback(() => {
    addToast({ text: "Không thể hiển thị file PDF của ebook", status: "inValid" });
  }, []);

  const handlePageInputCommit = () => {
    if (!manifest?.pageCount) {
      setPageInputValue(String(currentPage));

      return;
    }

    const parsedPage = Number(pageInputValue);

    if (!Number.isFinite(parsedPage)) {
      setPageInputValue(String(currentPage));

      return;
    }

    navigateToPage(parsedPage);
  };

  useEffect(() => {
    if (Number.isNaN(bookId) || !bookId) {
      navigate(RoutePath.HOME);

      return;
    }

    const previousTheme = StorageService.get(READING_THEME_KEY);

    if (
      !Number.isNaN(Number(previousTheme)) &&
      Number(previousTheme) >= ReadingScreenTheme.DEFAULT &&
      Number(previousTheme) <= ReadingScreenTheme.DARK
    ) {
      setTheme(Number(previousTheme));
    }

    initializeReader(bookId);
  }, [bookId]);

  useEffect(() => {
    if (!bookId || !manifest) {
      return;
    }

    const progressPercent = Math.min(
      100,
      Math.max(0, Math.round((currentPage / Math.max(1, manifest.pageCount)) * 100))
    );

    BookService.saveEbookProgress({
      bookId,
      currentPage,
      progressPercent,
      lastPositionJson: { page: currentPage },
    }).catch(() => undefined);
  }, [bookId, currentPage, manifest?.pageCount]);

  useEffect(() => {
    setPageInputValue(String(currentPage));
  }, [currentPage]);

  useEffect(() => {
    if (!session || !bookId) {
      return;
    }

    const timeout = window.setTimeout(() => {
      refreshSession(bookId).catch(() => undefined);
    }, Math.max(0, session.expiredAt - Date.now() - 30_000));

    return () => {
      window.clearTimeout(timeout);
    };
  }, [bookId, session?.expiredAt]);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    if (pdfLoading) {
      return;
    }

    const handleMouseUp = () => {
      const textLayer = getTextLayerElement();
      const selection = window.getSelection();

      if (!textLayer || !selection || !selection.rangeCount || selection.isCollapsed) {
        setSelectionState(null);

        return;
      }

      const range = selection.getRangeAt(0);

      if (!textLayer.contains(range.commonAncestorContainer)) {
        setSelectionState(null);

        return;
      }

      const rawText = selection.toString();
      const selectedText = rawText.replace(/\s+/g, " ").trim();

      if (!selectedText) {
        setSelectionState(null);

        return;
      }

      const preSelectionRange = range.cloneRange();

      preSelectionRange.selectNodeContents(textLayer);
      preSelectionRange.setEnd(range.startContainer, range.startOffset);

      const startOffset = preSelectionRange.toString().length;
      const endOffset = startOffset + rawText.length;
      const rect = range.getBoundingClientRect();

      // Dùng tọa độ viewport (fixed positioning) để tránh lỗi scroll/container
      const ICON_SIZE = 36;
      const HEADER_HEIGHT = 72;
      const spaceAbove = rect.top - HEADER_HEIGHT;
      const iconTop =
        spaceAbove > ICON_SIZE + 4
          ? rect.top - ICON_SIZE - 4
          : Math.min(rect.bottom + 4, window.innerHeight - ICON_SIZE - 4);

      setSelectionState({
        text: selectedText,
        startOffset,
        endOffset,
        top: iconTop,
        left: rect.left,
      });
    };

    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [currentPage, pdfLoading, textLayerVersion]);

  useEffect(() => {
    setSelectionState(null);
  }, [currentPage]);

  return (
    <div>
      <div className="fixed left-0 top-0 z-50 flex h-[72px] w-full items-center justify-between bg-[#272C36] pl-8 pr-6">
        <div
          className="flex size-6 cursor-pointer items-center justify-center rounded-full hover:bg-gray-500"
          onClick={handleBackClick}
        >
          <img src={Images.ArrowThinLeftIcon.default} alt="" />
        </div>
        <div>
          {!loading ? (
            <>
              <div className="text-center font-[SVN-Gilroy] text-xl font-bold text-white">
                {book?.name?.toUpperCase() ?? `TRANG ${currentPage}`}
              </div>
              <div className="mt-1 text-center font-[SVN-Gilroy] text-sm font-medium text-[#B0BAC9]">
                {manifest?.pageCount
                  ? (
                    <span className="flex items-center justify-center gap-2">
                      <span>Trang</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={pageInputValue}
                        className="h-7 w-[52px] rounded-md border border-[#4E586C] bg-transparent px-2 text-center text-white outline-none transition focus:border-[#5A82D7]"
                        onChange={(event) => {
                          const nextValue = event.target.value.replace(/\D/g, "");

                          setPageInputValue(nextValue);
                        }}
                        onBlur={handlePageInputCommit}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.currentTarget.blur();
                          }

                          if (event.key === "Escape") {
                            setPageInputValue(String(currentPage));
                            event.currentTarget.blur();
                          }
                        }}
                      />
                      <span>/ {manifest.pageCount}</span>
                    </span>
                  )
                  : "Đang tải trang..."}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center">
              <Skeleton className="h-7 w-40" />
              <Skeleton className="mt-2 h-4 w-24" />
            </div>
          )}
        </div>
        <div className="flex items-center gap-6">
          <div
            className={clsx(
              "flex h-[56px] w-[72px] cursor-pointer items-center justify-center rounded-full border border-solid border-[#FFF]",
              {
                "cursor-not-allowed opacity-40":
                  !book?.audio?.enabled,
              }
            )}
            onClick={handleListenBookClick}
          >
            <img src={Images.HeadPhone.default} alt="" />
          </div>
          <div>
            <img
              className="size-6 cursor-pointer hover:rounded-full hover:bg-gray-500"
              src={
                !isListViewShow
                  ? Images.ListViewCircle.default
                  : Images.ListViewShowing.default
              }
              alt=""
              onClick={handleListViewClick}
            />
          </div>
          <div>
            <img
              className="size-6 cursor-pointer hover:rounded-full hover:bg-gray-500"
              src={
                theme !== ReadingScreenTheme.DARK
                  ? Images.MoonIcon.default
                  : Images.MoonEnabledIcon.default
              }
              alt=""
              onClick={handleNightModeClick}
            />
          </div>
        </div>
      </div>

      <div
        className={clsx("relative min-h-[calc(100vh-72px)]", {
          [BgScreenThemeClass[ReadingScreenTheme.DEFAULT]]:
            theme === ReadingScreenTheme.DEFAULT,
          [BgScreenThemeClass[ReadingScreenTheme.GREEN]]:
            theme === ReadingScreenTheme.GREEN,
          [BgScreenThemeClass[ReadingScreenTheme.BROWN]]:
            theme === ReadingScreenTheme.BROWN,
          [BgScreenThemeClass[ReadingScreenTheme.DARK]]:
            theme === ReadingScreenTheme.DARK,
        })}
      >
        <div className="fixed top-1/2 right-[5%] flex h-[368px] w-[44px] flex-col justify-around md:right-[5%] lg:right-[5%] xl:right-[10%]">
          <div className="flex cursor-pointer flex-col items-center" onClick={handleLikeClick}>
            <div>
              <HeartIcon isLiked={book?.isFavorite} />
            </div>
            <div className="font-[SVN-Gilroy] text-sm font-medium text-[#333B47]">
              {book?.insight?.likeCount ?? 0}
            </div>
          </div>
          <div
            className="flex cursor-pointer flex-col items-center"
            onClick={() => {
              setTypeSideBarList("note");
              setIsListViewShow(true);
            }}
          >
            <div>
              <img src={Images.CommentIcon.default} alt="" />
            </div>
            <div className="font-[SVN-Gilroy] text-sm font-medium text-[#333B47]">
              {sortedNotes.length}
            </div>
          </div>
          <div className="flex cursor-pointer flex-col items-center">
            <div>
              <img src={Images.View.default} alt="" />
            </div>
            <div className="font-[SVN-Gilroy] text-sm font-medium text-[#333B47]">
              {book?.insight?.viewCount ?? 0}
            </div>
          </div>
          <div className="flex cursor-pointer flex-col items-center">
            <div>
              <img src={Images.HeadPhoneDetail.default} alt="" />
            </div>
            <div className="font-[SVN-Gilroy] text-sm font-medium text-[#333B47]">
              {book?.insight?.listenCount ?? 0}
            </div>
          </div>
          <div className="flex cursor-pointer flex-col items-center">
            <div>
              <img src={Images.Share.default} alt="" />
            </div>
            <div className="font-[SVN-Gilroy] text-sm font-medium text-[#333B47]">
              {book?.insight?.shareCount ?? 0}
            </div>
          </div>
        </div>

        <div className="fixed left-8 top-1/2 h-[calc(100vh-72px)]">
          <img
            className={clsx("cursor-pointer", {
              "!cursor-not-allowed": currentPage <= 1,
            })}
            src={Images.LeftArrowIcon.default}
            alt=""
            onClick={handlePreviousClick}
          />
        </div>

        <div className="mx-auto w-[920px] pb-5 pt-24">
          {!loading ? (
            <div
              ref={pageContainerRef}
              className="relative w-full select-text"
              onContextMenu={(event) => {
                event.preventDefault();
              }}
            >
              {pdfLoading ? (
                <div className="rounded-[8px] bg-white p-6 shadow-sm">
                  <Skeleton className="h-[1200px] w-full" />
                </div>
              ) : (
                <MemoizedPdfPage
                  pdfUrl={pdfUrl}
                  pageNumber={currentPage}
                  onRenderSuccess={handleTextLayerSuccess}
                  onLoadError={handlePdfLoadError}
                />
              )}
            </div>
          ) : (
            <Skeleton className="h-full" count={30} />
          )}

          {!!selectionState && (
            <div
              className="fixed z-50 flex size-9 cursor-pointer items-center justify-center rounded-full bg-[#5A82D7]"
              style={{
                top: selectionState.top,
                left: selectionState.left,
              }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={openCreateNoteModal}
            >
              <TbEdit className="text-xl text-white" />
            </div>
          )}
        </div>

        <div className="fixed right-8 top-1/2 h-[calc(100vh-72px)]">
          <img
            className={clsx("cursor-pointer", {
              "!cursor-not-allowed": currentPage >= (manifest?.pageCount ?? 1),
            })}
            src={Images.RightArrowIcon.default}
            alt=""
            onClick={handleNextClick}
          />
        </div>
      </div>

      <div
        className={clsx(
          "fixed z-[60] -right-[340px] top-[72px] h-[calc(100vh-72px)] max-h-[calc(100vh-72px)] w-[340px] overflow-y-auto overscroll-contain bg-[#272C36] transition-all",
          {
            "!right-0": !!isListViewShow,
          }
        )}
      >
        <div className="relative z-10 p-6 pb-4 pt-5 text-[#171A20]">
          <div className="flex items-center justify-between text-[20px] font-bold text-white">
            Danh sách{" "}
            <IoClose className="cursor-pointer" onClick={handleListViewClick} />
          </div>
          <div className="mt-2 flex gap-[16px]">
            <p
              className={clsx(
                "text-[16px] font-semibold",
                typeSideBarList === "chapter"
                  ? "text-[#5A82D7]"
                  : "cursor-pointer text-[#FFF]"
              )}
              onClick={() => setTypeSideBarList("chapter")}
            >
              Mục lục
            </p>
            <p
              className={clsx(
                "text-[16px] font-semibol",
                typeSideBarList === "note"
                  ? "text-[#5A82D7]"
                  : "cursor-pointer text-[#FFF]"
              )}
              onClick={() => setTypeSideBarList("note")}
            >
              Ghi chú
            </p>
            <p
              className={clsx(
                "text-[16px] font-semibol",
                typeSideBarList === "highlight"
                  ? "text-[#5A82D7]"
                  : "cursor-pointer text-[#FFF]"
              )}
              onClick={() => setTypeSideBarList("highlight")}
            >
              Lời dẫn
            </p>
          </div>
          <hr className="absolute bottom-1 left-[18px] w-[304px] border-t-[#4E586C]" />
        </div>

        <div className="px-6 py-2">
          {typeSideBarList === "chapter" &&
            (manifest?.pages ?? []).map((item) => (
              <div
                key={item.resourceId}
                className={clsx(
                  "cursor-pointer rounded-lg py-2 font-[SVN-Gilroy] font-medium tracking-[0.024px] text-[#DFE2E7] hover:bg-gray-500",
                  {
                    "!text-[#38499b]": item.page === currentPage,
                  }
                )}
                onClick={() => {
                  setCurrentPage(item.page);
                  setActiveNoteId(null);
                  clearSelection();
                }}
              >
                Trang {item.page}
              </div>
            ))}

          {typeSideBarList === "note" && (
            <div className="space-y-3">
              {sortedNotes.map((note) => (
                <div
                  key={note.id}
                  className={clsx("rounded-lg p-3 cursor-pointer", {
                    "bg-[#374151]": activeNoteId !== note.id,
                    "bg-[#4B5563]": activeNoteId === note.id,
                  })}
                  onClick={() => handleNavigateToNote(note)}
                >
                  <div className="mb-1 text-xs font-semibold text-[#8BB3FF]">
                    Trang {note.pageNo}
                  </div>
                  <p className="text-[14px] font-medium text-[#DFE2E7]">
                    {note.textSelected}
                  </p>
                  <p className="mt-1 text-[14px] font-medium text-[#B0BAC9]">
                    {note.textNote}
                  </p>
                  <div className="mt-3 flex gap-3 text-xs font-semibold">
                    <button
                      className="text-[#8BB3FF]"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleEditNote(note);
                      }}
                    >
                      Sửa
                    </button>
                    <button
                      className="text-[#FF9B9B]"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteNote(note.id);
                      }}
                    >
                      Xóa
                    </button>
                  </div>
                </div>
              ))}

              {!sortedNotes.length && (
                <div className="rounded-lg border border-dashed border-[#4E586C] p-3 text-sm text-[#B0BAC9]">
                  Chưa có ghi chú nào cho ebook này.
                </div>
              )}
            </div>
          )}

          {typeSideBarList === "highlight" && (
            <div className="rounded-lg border border-dashed border-[#4E586C] p-3 text-sm text-[#B0BAC9]">
              Ebook không có lời dẫn đồng bộ theo audio.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EbookReadingPage;
